'use client';

import { useSearchParams, useRouter } from 'next/navigation';
import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback, useMemo } from 'react';

import { CommonUtils } from '../../(common-lib)/CommonUtils';
import { AccessControl } from '../../(common-lib)/AccessControl';
import { LoanAppData } from '../../(common-lib)/LoanAppData';
import { CustomFieldDefinition, DocumentUploaded, getLoanStatus, UserRoleType } from '../../(common-lib)/LoanData';
import { formatPropertyAddress } from '../../(common-lib)/MailUtils';
import { Permissions, Roles } from '../../(common-lib)/RolesPermissions';
import { useUserContext } from '../../(context)/UserContext';
import { initializeGovtIssuedIdDocs } from '../(ui-lib)/GovtIssuedIdDoc';
import { initializeLiquidityDocs } from '../(ui-lib)/Liquidity';
import { copyLoanDataToDocument, copyLoanDataToField, getApplicationSections } from '../(ui-lib)/Application';
import { applicationSections } from '../(ui-lib)/ApplicationData';
import { ApplicationSection, SectionGroup, isSectionGroup } from '../(ui-lib)/ApplicationTypes';
import { DocumentMappingService } from '../(ui-lib)/Document';
import { FormDataUtils } from '../../(ui-lib)/FormDataUtils';
import StorageClient from '../../(ui-lib)/StorageClient';
import { UserClient } from '../../(ui-lib)/UserClient';
import { initializeSOWData, normalizeSOWData, switchPropertyType } from '../sow/sow';
import { mapLoanDataPropertyTypeToSOW } from '../sow/sow/sow.types';
import type { SOWData } from '../sow/sow/sow.types';

interface Task {
  id: number;
  title: string;
  description: string;
  status: 'needs_revision' | 'in_progress' | 'pending' | 'completed';
  priority: 'high' | 'medium' | 'low';
  completedSteps: number;
  totalSteps: number;
}

interface PortalContextType {
  tasks: Task[];
  loanData: LoanAppData | undefined;
  setLoanData: React.Dispatch<React.SetStateAction<LoanAppData | undefined>>;
  sections: (ApplicationSection | SectionGroup)[];
  setSections: React.Dispatch<React.SetStateAction<(ApplicationSection | SectionGroup)[]>>;
  sectionsLoading: boolean;
  updateTaskStatus: (taskId: number, status: Task['status']) => void;
  updateTaskProgress: (taskId: number, completedSteps: number) => void;
  refreshData: () => Promise<boolean>;
  isLoading: boolean;
  fieldIdToValue: Record<string, string>;
  setFieldIdToValue: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  docIdToUploadedDoc: Record<string, DocumentUploaded | undefined>;
  setDocIdToUploadedDoc: React.Dispatch<React.SetStateAction<Record<string, DocumentUploaded | undefined>>>;
  documentsReady: boolean;
  selectedLoanId: string | null;
  userLoanRole: UserRoleType | null;
  sowData: SOWData | undefined;
  setSowData: React.Dispatch<React.SetStateAction<SOWData | undefined>>;
  sowDirty: boolean;
  setSowDirty: React.Dispatch<React.SetStateAction<boolean>>;
  canManageCustomFields: boolean;
  customFieldDefinitions: CustomFieldDefinition[];
  setCustomFieldDefinitions: React.Dispatch<React.SetStateAction<CustomFieldDefinition[]>>;
}

const PortalContext = createContext<PortalContextType | undefined>(undefined);
export function usePortalContext() {
  const context = useContext(PortalContext);
  if (context === undefined) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
}

interface PortalProviderProps {
  children: ReactNode;
}

const VALID_USER_ROLE_KEYS = new Set<string>(Object.keys(Roles));

export function PortalProvider({ children }: PortalProviderProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { userLoans, isLoadingLoans, roles } = useUserContext();
  const accessControl = AccessControl.getInstance();
  const canManageCustomFields = useMemo(
    () => accessControl.hasPermissionForAnyRole(roles, Permissions.DOCUMENTS_ADD),
    [accessControl, roles]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loanData, setLoanData] = useState<LoanAppData | undefined>();
  const [loanStatus, setLoanStatus] = useState<string | undefined>();
  const [loanPurpose, setLoanPurpose] = useState<string | undefined>();
  const [exitStrategy, setExitStrategy] = useState<string | undefined>();
  const [propertyType, setPropertyType] = useState<string | undefined>();
  const [sections, setSections] = useState<(ApplicationSection | SectionGroup)[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [fieldIdToValue, setFieldIdToValue] = useState<Record<string, any>>({});
  const [docIdToUploadedDoc, setDocIdToUploadedDoc] = useState<Record<string, DocumentUploaded | undefined>>({});
  const [documentsReady, setDocumentsReady] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [userLoanRole, setUserLoanRole] = useState<UserRoleType | null>(null);
  const [sowData, setSowData] = useState<SOWData | undefined>();
  const [sowDirty, setSowDirty] = useState(false);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([]);
  // Keep a ref to the selectedLoanId so we can provide a stable refreshData function
  const selectedLoanIdRef = useRef<string | null>(selectedLoanId);
  useEffect(() => {
    selectedLoanIdRef.current = selectedLoanId;
  }, [selectedLoanId]);

  const resetLoanState = useCallback((selectedId: string | null = null) => {
    setSelectedLoanId(selectedId);
    setTasks([]);
    setLoanData(undefined);
    setLoanStatus(undefined);
    setLoanPurpose(undefined);
    setExitStrategy(undefined);
    setPropertyType(undefined);
    setCustomFieldDefinitions([]);
    setSowData(undefined);
    setSowDirty(false);
    setSections([]);
    setFieldIdToValue({});
    setDocIdToUploadedDoc({});
    setDocumentsReady(false);
    setUserLoanRole(null);
  }, []);

  const handleMissingLoanData = useCallback((loanId: string) => {
    CommonUtils.logWarn('PortalContext: No loan data returned', { loanId });
    resetLoanState();
    router.push('/loanapp');
  }, [resetLoanState, router]);

  const handleLoanFetchError = useCallback((error: any, loanId: string) => {
    const status = typeof error?.status === 'number' ? error.status : error?.response?.status;
    if (status === 404 || status === 403) {
      handleMissingLoanData(loanId);
      return;
    }
    CommonUtils.logError('PortalContext: Error fetching loan data', { loanId, error });
  }, [handleMissingLoanData]);

  const fetchAndSetUserLoanRole = useCallback(async (loanId: string, logPrefix: string): Promise<UserRoleType> => {
    try {
      const roleResponse = await UserClient.getEffectiveLoanRole(loanId);
      const role = String(roleResponse?.success ? roleResponse?.role : '').trim();
      if (!role || !VALID_USER_ROLE_KEYS.has(role)) {
        throw new Error('Effective loan role is unavailable');
      }
      return role as UserRoleType;
    } catch (error) {
      CommonUtils.logError(`${logPrefix}: Error fetching effective loan role`, error);
      throw error;
    }
  }, []);

  // Single loan-loading path used by both auto-load (loanId changes) and manual refresh.
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  const refreshData = useCallback(async (logPrefix = 'PortalContext.refreshData'): Promise<boolean> => {
    // If a refresh is already in progress, return the in-flight promise.
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const id = selectedLoanIdRef.current;
    if (!id) return false;

    setIsLoading(true);
    const promise = (async () => {
      try {
        const [data, roleResult] = await Promise.all([
          StorageClient.downloadLoanData(id),
          fetchAndSetUserLoanRole(id, logPrefix)
            .then((role) => ({ role, error: null }))
            .catch((error) => ({ role: null, error }))
        ]);
        if (roleResult.error) {
          CommonUtils.logWarn(`${logPrefix}: Proceeding without effective loan role`, {
            loanId: id,
            error: roleResult.error,
          });
        }
        setUserLoanRole(roleResult.role);
        if (data) {
          setTasks(data.tasks || []);
          setLoanData(new LoanAppData(data));
          setLoanStatus(data.loanStatus?.display ?? data.loanStatus);
          setLoanPurpose(data.loanPurpose?.display ?? data.loanPurpose);
          setExitStrategy(data.exitStrategy?.display ?? data.exitStrategy);
          setPropertyType(data.propertyType?.display ?? data.propertyType);
          setCustomFieldDefinitions(Array.isArray(data.customFieldDefinitions) ? data.customFieldDefinitions : []);
          return true;
        }
        handleMissingLoanData(id);
        return false;
      } catch (error) {
        handleLoanFetchError(error, id);
        return false;
      } finally {
        setIsLoading(false);
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = promise;
    return promise;
  }, [fetchAndSetUserLoanRole, handleLoanFetchError, handleMissingLoanData]);

  // Only refetch loan data when the effective loanId changes,
  // not for unrelated query params like `section`.
  const rawLoanIdParam = searchParams?.get('loanid') || null;
  const loanIdParam = rawLoanIdParam?.trim() ? rawLoanIdParam.trim() : null;
  useEffect(() => {
    const fetchLoanData = async () => {
      if (!loanIdParam) {
        resetLoanState();
        setIsLoading(false);
        return;
      }

      if (isLoadingLoans || userLoans == null) {
        setIsLoading(true);
        return;
      }

      const userHasLoan = userLoans?.some((loan: any) => {
        const publicLoanId = String(
          loan?.crmData?.uniqueLoanId
          || loan?.dbRecord?.loan_id
          || loan?.loan_id
          || ''
        ).trim();
        return publicLoanId === loanIdParam;
      });

      if (!userHasLoan) {
        resetLoanState();
        setIsLoading(false);
        router.push('/loanapp');
        return;
      }

      try {
        const effectiveLoanId = loanIdParam;
        if (effectiveLoanId) {
          setSelectedLoanId(effectiveLoanId);
          selectedLoanIdRef.current = effectiveLoanId;
          await refreshData('PortalContext');
        }
      } catch (error) {
        handleLoanFetchError(error, loanIdParam ?? '');
      }
    };
    fetchLoanData();
  }, [loanIdParam, userLoans, isLoadingLoans, router, resetLoanState, handleLoanFetchError, refreshData]);

  useEffect(() => {
    if (!loanData) {
      setCustomFieldDefinitions([]);
      return;
    }
    const raw = loanData.getRawLoanData() as any;
    // Some save payloads omit this array; keep the existing definitions instead of briefly hiding miscellaneous.
    if (Array.isArray(raw.customFieldDefinitions)) {
      setCustomFieldDefinitions(raw.customFieldDefinitions);
    }
  }, [loanData]);

  // Get the sections based on the loan purpose, exit strategy and property type
  useEffect(() => {
    setSectionsLoading(true);
    const rawLoanData = loanData?.getRawLoanData() as any;
    const loanStatusBeforeCancel = rawLoanData?.loanStatusBeforeCancel || rawLoanData?.previousLoanStatus;
    const effectiveLoanStatus = (
      loanStatus === 'CANCELLED'
      && typeof loanStatusBeforeCancel === 'string'
      && loanStatusBeforeCancel.trim()
    )
      ? loanStatusBeforeCancel
      : loanStatus;
    const normalizedStage = getLoanStatus(effectiveLoanStatus);
    const stage = normalizedStage === 'Unknown' ? 'Draft' : normalizedStage;
    const projectSummary = rawLoanData?.projectSummary;
    const projectSummaryAboveGroundSqft = {
      current: projectSummary?.project_above_current_sqft
        ? Number(projectSummary.project_above_current_sqft)
        : undefined,
      rehabbed: projectSummary?.project_above_rehab_sqft
        ? Number(projectSummary.project_above_rehab_sqft)
        : undefined,
    };
    getApplicationSections(
      applicationSections,
      loanPurpose,
      exitStrategy,
      propertyType,
      stage,
      projectSummaryAboveGroundSqft,
      rawLoanData?.fastTrack === true
    )
      .then(result => {
        setSections(result);
        DocumentMappingService.getInstance().initializeFromFilteredSections(result);
        setSectionsLoading(false);
      })
      .catch(error => {
        CommonUtils.logError('Error loading application sections', error);
        setSectionsLoading(false);
      });
  }, [loanStatus, loanPurpose, exitStrategy, propertyType, loanData]);

  useEffect(() => {
    if (sectionsLoading || sections.length === 0) {
      return;
    }

    const activeCustomFields = customFieldDefinitions.filter((field) => !field.deletedAt);
    const shouldIncludeMisc = canManageCustomFields || activeCustomFields.length > 0;
    if (!shouldIncludeMisc) {
      return;
    }

    const hasMiscSection = sections.some((section) => !isSectionGroup(section) && section.id === 'miscellaneous');
    if (hasMiscSection) {
      return;
    }

    const miscTemplate = applicationSections.find(
      (section) => !isSectionGroup(section) && section.id === 'miscellaneous'
    ) as ApplicationSection | undefined;
    if (!miscTemplate) {
      return;
    }

    const nextSections = [
      ...sections,
      {
        ...miscTemplate,
        fields: [...miscTemplate.fields],
        documents: miscTemplate.documents ? [...miscTemplate.documents] : []
      }
    ];

    setSections(nextSections);
    DocumentMappingService.getInstance().initializeFromFilteredSections(nextSections);
  }, [sections, sectionsLoading, customFieldDefinitions, canManageCustomFields]);

  useEffect(() => {
    function initializeFieldIdToValue(sections: (ApplicationSection | SectionGroup)[], loanData: LoanAppData, fieldIdToValue: Record<string, any>) {
      sections.forEach(section => {
        if ('sections' in section) {
          initializeFieldIdToValue(section.sections, loanData, fieldIdToValue);
        } else if ('fields' in section) {
          section.fields.forEach(field => {
            fieldIdToValue[field.id] = copyLoanDataToField(loanData!, field.id)
          });
        }
      });
      return fieldIdToValue;
    }

    function initializeDocIdToUploadedDoc(sections: (ApplicationSection | SectionGroup)[], loanData: LoanAppData, docIdToUploadedDoc: Record<string, DocumentUploaded | undefined>) {
      sections.forEach(section => {
        if ('sections' in section) {
          initializeDocIdToUploadedDoc(section.sections, loanData, docIdToUploadedDoc);
        } else if ('documents' in section) {
          if (initializeLiquidityDocs(section.id, loanData, section.documents ?? [], docIdToUploadedDoc, copyLoanDataToDocument)) return;
          if (initializeGovtIssuedIdDocs(section.id, loanData, docIdToUploadedDoc, copyLoanDataToDocument)) return;
          section.documents?.forEach(document => {
            docIdToUploadedDoc[document.id] = copyLoanDataToDocument(loanData!, document.id)
          });
        }
      });
      return docIdToUploadedDoc;
    }

    // Only run if both are available and non-empty
    if (!loanData || !sections || sections.length === 0) {
      setDocumentsReady(false);
      return;
    }

    const fieldIdToValue: Record<string, any> = {};
    initializeFieldIdToValue(sections, loanData, fieldIdToValue);
    setFieldIdToValue(fieldIdToValue);

    try {
      const docIdToUploadedDoc: Record<string, DocumentUploaded | undefined> = {};
      initializeDocIdToUploadedDoc(sections, loanData, docIdToUploadedDoc);
      setDocIdToUploadedDoc(docIdToUploadedDoc);
      setDocumentsReady(true);

      const raw = loanData.getRawLoanData() as any;
      const rawSow = raw?.projectSummary?.scopeOfWork;

      // Map loanData propertyType to SOW PropertyType
      const loanPropertyType = raw?.propertyType;
      const sowPropertyType = mapLoanDataPropertyTypeToSOW(loanPropertyType);

      // Normalize SOW data, ensuring property type matches loan data
      const normalizedSow = normalizeSOWData(rawSow ?? {});

      // If property type doesn't match, switch it (this preserves building-wide data)
      const sowWithCorrectPropertyType = normalizedSow.propertyType !== sowPropertyType
        ? switchPropertyType(normalizedSow, sowPropertyType)
        : normalizedSow;

      const propertyDetails = loanData.getPropertyDetails?.() ?? {};
      const state = propertyDetails?.propertyState?.key || propertyDetails?.propertyState?.display;
      const formattedAddress = formatPropertyAddress(propertyDetails?.propertyAddress, state);
      const nextSow = formattedAddress && formattedAddress.trim()
        && formattedAddress.trim() !== sowWithCorrectPropertyType.propertyAddress?.trim()
        ? { ...sowWithCorrectPropertyType, propertyAddress: formattedAddress }
        : sowWithCorrectPropertyType;
      setSowData(nextSow);
      setSowDirty(false);
    } catch (error) {
      CommonUtils.logError('Error initializing SOW data', error);
      // Initialize with correct property type from loan data
      const raw = loanData.getRawLoanData() as any;
      const sowPropertyType = mapLoanDataPropertyTypeToSOW(raw?.propertyType);
      setSowData(initializeSOWData(sowPropertyType));
      setSowDirty(false);
    }
  }, [loanData, sections]);

  const propertyTypeFieldValue = typeof fieldIdToValue.property_type === 'string'
    ? fieldIdToValue.property_type
    : fieldIdToValue.property_type?.display ?? fieldIdToValue.property_type?.key;
  const currentSowPropertyType = sowData?.propertyType;

  // Watch for property_type field changes and update SOW data accordingly
  useEffect(() => {
    if (!currentSowPropertyType || !propertyTypeFieldValue) return;

    // Convert the field value to LoanData PropertyType
    const updatedLoanPropertyType = FormDataUtils.stringToPropertyType(propertyTypeFieldValue);

    // Map to SOW PropertyType
    const newSowPropertyType = mapLoanDataPropertyTypeToSOW(updatedLoanPropertyType);

    // Only update if property type actually changed
    if (currentSowPropertyType !== newSowPropertyType) {
      setSowData((prevSow) => {
        if (!prevSow) return prevSow;
        return switchPropertyType(prevSow, newSowPropertyType);
      });
      setSowDirty(true);
    }
  }, [propertyTypeFieldValue, currentSowPropertyType, setSowData, setSowDirty]);

  const updateTaskStatus = (taskId: number, status: Task['status']) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId ? { ...task, status } : task
      )
    );
  };

  const updateTaskProgress = (taskId: number, completedSteps: number) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId ? { ...task, completedSteps } : task
      )
    );
  };

  const value: PortalContextType = {
    tasks,
    loanData,
    setLoanData,
    sections,
    setSections,
    sectionsLoading,
    updateTaskStatus,
    updateTaskProgress,
    refreshData,
    isLoading,
    fieldIdToValue,
    setFieldIdToValue,
    docIdToUploadedDoc,
    setDocIdToUploadedDoc,
    documentsReady,
    selectedLoanId,
    userLoanRole,
    sowData,
    setSowData,
    sowDirty,
    setSowDirty,
    canManageCustomFields,
    customFieldDefinitions,
    setCustomFieldDefinitions,
  };

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
}
